export enum FeatureKey {
  SOCIAL_FEED = 'social_feed',
  ARTICLES = 'articles',
  DISCOVERY = 'discovery',
  CREATOR_PROFILE = 'creator_profile',
  FOLLOW_SYSTEM = 'follow_system',
  DIRECT_MESSAGES = 'direct_messages',
  CREATOR_STUDIO = 'creator_studio',
  AUDIENCE = 'audience',
  CIRCLES = 'circles',
  NEWSLETTERS = 'newsletters',
  EMAIL_MARKETING = 'email_marketing',
  AFFILIATE_MANAGER = 'affiliate_manager',
  REFERRAL_SYSTEM = 'referral_system',
  ANALYTICS = 'analytics',
  AI_ASSISTANT = 'ai_assistant',
  AI_CONTENT = 'ai_content',
  AI_AUTOMATION = 'ai_automation',
  CUSTOM_DOMAIN = 'custom_domain',
  COURSES = 'courses',
  MEMBERSHIPS = 'memberships',
  EVENTS = 'events',
  OFFERS = 'offers',
  CREATOR_PAYOUTS = 'creator_payouts',
  LIVE_STREAMING = 'live_streaming',
  MOBILE_APP = 'mobile_app',
  PUBLIC_API = 'public_api',
  WEBHOOKS = 'webhooks',
  CREATOR_SITE = 'creator_site',
}

export enum FeatureStage {
  EXPERIMENTAL = 'experimental',
  ALPHA = 'alpha',
  BETA = 'beta',
  EARLY_ACCESS = 'early_access',
  GENERAL_AVAILABILITY = 'general_availability',
  DEPRECATED = 'deprecated',
}

export enum FeatureCategory {
  SOCIAL = 'social',
  COMMERCE = 'commerce',
  CREATOR_OS = 'creator_os',
  AI = 'ai',
  ANALYTICS = 'analytics',
  INTEGRATIONS = 'integrations',
  PLATFORM = 'platform',
  MOBILE = 'mobile',
}

export enum FeatureVisibility {
  INTERNAL = 'internal',
  CREATORS = 'creators',
  CONSUMERS = 'consumers',
  ADMIN = 'admin',
  PUBLIC = 'public',
}

export type StudioModuleProfile = 'social-commerce' | 'studio-only' | 'backlog';

import { FeatureRequirements } from './requirements';

export interface FeatureFlag {
  key: FeatureKey;
  name: string;
  description: string;
  category: FeatureCategory;
  defaultState: boolean;
  stage: FeatureStage;
  visibility: FeatureVisibility;
  /**
   * True only for Creator Studio modules that platform admins may override
   * for an eligible creator. Social, identity, plan, and payout capabilities
   * must never opt in.
   */
  creatorOverrideable?: boolean;
  /** Declarative gating metadata; enforced when matching resolvers are registered. */
  requirements?: FeatureRequirements;
  /**
   * When true, platform admins can enable/disable independently for members vs creators
   * on the social app. Studio and platform flags use a single global toggle.
   */
  audienceScopable?: boolean;
  /**
   * Reserved backlog capability — stays off globally, hidden from Studio, and shown
   * with a backlog badge in Admin feature flags only.
   */
  studioBacklog?: boolean;
  /**
   * Studio access profile when the module is creator-overrideable.
   * social-commerce: public discovery preserved; suspended when off with inventory.
   */
  moduleProfile?: StudioModuleProfile;
}

/** Legacy DB / env keys migrated to canonical FeatureKey values. */
export const LEGACY_FEATURE_KEY_ALIASES: Readonly<Record<string, FeatureKey>> = {
  communities: FeatureKey.CIRCLES,
  services: FeatureKey.OFFERS,
  digital_products: FeatureKey.OFFERS,
};

export enum FeatureAudience {
  MEMBER = 'member',
  CREATOR = 'creator',
}

export type PlatformAudienceOverrides = Readonly<
  Partial<Record<FeatureKey, { member?: boolean | null; creator?: boolean | null }>>
>;

export type FeatureRegistry = Readonly<Record<FeatureKey, FeatureFlag>>;
export type FeatureOverrideValue = boolean | string | undefined;
export type FeatureOverrides = Partial<Record<FeatureKey, FeatureOverrideValue>> &
  Record<string, FeatureOverrideValue>;
